"use client"

import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { minutesToTime, timeToMinutes } from "@/lib/convex-commitments"
import { peakWindowFor, type FocusChoice, type HoursDraft } from "@/lib/convex-day-shape"
import { useOnboardingTranslations } from "@/hooks/use-translations"

/**
 * "When does your day actually run?" — the second onboarding question the
 * planner cannot work without.
 *
 * `workdayStartMinutes` / `workdayEndMinutes` have been readable by the planner
 * since it was built, and Settings → Preferences can write them, but nobody
 * goes to Settings before using an app for the first time. Every user who never
 * went looking has therefore had their day packed into a hardcoded 9–18,
 * including the ones who start at six or work evenings.
 *
 * The focus window is asked as a *part of the day* rather than as two more
 * clock fields: nobody knows their peak to the quarter hour, and expressing it
 * relative to the working window means it stays sensible whatever hours they
 * set here. The mapping itself lives in `lib/convex-day-shape` with the shape
 * it produces.
 */

const FOCUS_CHOICES: FocusChoice[] = ["early", "midday", "late", "none"]

interface HoursStepProps {
  value: HoursDraft
  onChange: (next: HoursDraft) => void
}

export function HoursStep({ value, onChange }: HoursStepProps) {
  const t = useOnboardingTranslations()

  const setTime = (key: "workdayStartMinutes" | "workdayEndMinutes", raw: string) => {
    const minutes = timeToMinutes(raw)
    if (minutes === null) return
    onChange({ ...value, [key]: minutes })
  }

  const invalid = value.workdayEndMinutes <= value.workdayStartMinutes
  const peak = peakWindowFor(value)

  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor="onboarding-workday-start">{t("hours_step_start")}</Label>
          <Input
            id="onboarding-workday-start"
            type="time"
            value={minutesToTime(value.workdayStartMinutes)}
            onChange={(event) => setTime("workdayStartMinutes", event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="onboarding-workday-end">{t("hours_step_end")}</Label>
          <Input
            id="onboarding-workday-end"
            type="time"
            value={minutesToTime(value.workdayEndMinutes)}
            onChange={(event) => setTime("workdayEndMinutes", event.target.value)}
          />
        </div>
      </div>

      {invalid && <p className="text-xs text-destructive">{t("hours_step_invalid")}</p>}

      <div className="space-y-2">
        <Label>{t("hours_step_focus_label")}</Label>
        <div className="flex flex-wrap gap-2">
          {FOCUS_CHOICES.map((choice) => {
            const active = value.focus === choice
            return (
              <button
                key={choice}
                type="button"
                aria-pressed={active}
                onClick={() => onChange({ ...value, focus: choice })}
                className={cn(
                  "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors",
                  active
                    ? "border-primary bg-primary/10 text-foreground"
                    : "border-input text-muted-foreground hover:bg-muted",
                )}
              >
                {t(`hours_step_focus_${choice}`)}
                {active && <Check className="h-3.5 w-3.5 text-primary" />}
              </button>
            )
          })}
        </div>
      </div>

      {/* Showing the derived window makes the abstract choice concrete, and is
          the only way the user can tell "morning" meant 06:00–09:00 for them. */}
      <p className="text-xs text-muted-foreground">
        {peak && !invalid
          ? `${t("hours_step_focus_result")} ${minutesToTime(peak.peakStartMinutes)}–${minutesToTime(peak.peakEndMinutes)}`
          : t("hours_step_hint")}
      </p>
    </div>
  )
}
